import { AUTH_LEARNER_CREATED } from "@outschool/auth-shared";
import { ChildFragmentFragment } from "@outschool/gql-frontend-generated";
import { useLocalStorageState } from "@outschool/local-storage";
import { useTranslation } from "@outschool/localization";
import { useSession } from "@outschool/ui-auth";
import Cookies from "js-cookie";
import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
} from "react";

import { useActiveLearner } from "./ActiveLearnerProvider";

const LOCAL_STORAGE_KEY = "searchByLearnerUid";

interface SearchByLearnerContextValue {
  showLearnerFilter: boolean;
  learner: ChildFragmentFragment | null;
  setLearner: (learner: ChildFragmentFragment | null) => void;
}

const SearchByLearnerContext = createContext<SearchByLearnerContextValue>({
  showLearnerFilter: false,
  learner: null,
  setLearner: () => {},
});

interface SearchByLearnerProviderProps {
  children: ReactNode;
}

// We use a sentinel value to indicate that the learner has not been set, so we can distinguish between
// the case where the learner has been set to null and the case where the learner has not been set at all.
// This means that "null" indicates that any/all learners are selected.
// This is useful, for example, when the user makes a search from the global search bar with "All learners" selected.
// In that case, we don’t want to default them to a learner because they’ve explicitly selected "All learners".
const SENTINEL_VALUE = "__SENTINEL__";
function learnerSet(learnerSearchValue: string | null) {
  if (learnerSearchValue === SENTINEL_VALUE) {
    return false;
  }
  return true;
}

/**
 * Learner-Specific Search Provider.
 */
export function SearchByLearnerProvider({
  children,
}: SearchByLearnerProviderProps) {
  const { setActiveLearner } = useActiveLearner();
  const [learnerUid, saveLearnerUid] = useLocalStorageState<string | null>(
    LOCAL_STORAGE_KEY,
    SENTINEL_VALUE,
  );
  const { currentUser } = useSession();

  const showLearnerFilter = Boolean(currentUser?.children?.length);

  const learner = React.useMemo(
    () =>
      currentUser?.children.find(child => child?.uid === learnerUid) ?? null,
    [currentUser?.children, learnerUid],
  );

  const setLearner = useCallback(
    (learner: ChildFragmentFragment | null) => {
      saveLearnerUid(learner?.uid ?? null);
    },
    [saveLearnerUid],
  );

  // Initialize learner selection based on different scenarios
  const initializeLearner = useCallback(() => {
    // Priority 1: Learner just created during signup/onboarding
    const isLearnerCreated = Cookies.get(AUTH_LEARNER_CREATED);
    if (isLearnerCreated) {
      const createdLearner = currentUser?.children.at(0);
      if (createdLearner) {
        setLearner(createdLearner);
        setActiveLearner(createdLearner.uid);
      }
      Cookies.remove(AUTH_LEARNER_CREATED);
      return;
    }

    // Priority 2: Auto-select last created child
    if (currentUser && !learnerSet(learnerUid)) {
      const lastCreatedChild = currentUser.children.at(-1);
      if (lastCreatedChild) {
        setLearner(lastCreatedChild);
      }
    }
    // We have to ignore updates to the learner here, otherwise we’ll continually initialize the learner if the user
    // tries to set the learner to null, e.g.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.children, setLearner, setActiveLearner]);

  useEffect(() => {
    initializeLearner();
  }, [initializeLearner]);

  return (
    <SearchByLearnerContext.Provider
      value={{
        showLearnerFilter,
        learner,
        setLearner,
      }}
    >
      {children}
    </SearchByLearnerContext.Provider>
  );
}

/**
 * Note: This hook is currently disabled. We plan to re-enable Learner-Specific Search in the future.
 */
export function useSearchByLearner({
  onFilterChange,
}: {
  onFilterChange?: (learner: ChildFragmentFragment | null) => void;
} = {}) {
  const { t } = useTranslation("client\\providers\\SearchByLearnerProvider");
  const context = useContext(SearchByLearnerContext);
  const onFilterChangeRef = React.useRef(onFilterChange);
  const initializedRef = React.useRef(false);

  if (context === undefined) {
    throw new Error(
      "useSearchByLearner must be used within a SearchByLearnerProvider",
    );
  }

  const { learner } = context;

  useEffect(() => {
    onFilterChangeRef.current = onFilterChange;
  }, [onFilterChange]);

  useEffect(() => {
    if (!initializedRef.current) {
      initializedRef.current = true;
      return;
    }

    if (onFilterChangeRef.current) {
      onFilterChangeRef.current(learner);
    }
  }, [learner]);

  const getSearchBarPlaceholder = (_learner: ChildFragmentFragment | null) => {
    return t("What’s your child’s interest?");
  };

  return {
    showLearnerFilter: false,
    learner: null as ChildFragmentFragment | null,
    setLearner: (_learner: ChildFragmentFragment | null) => {},
    searchBarPlaceholder: getSearchBarPlaceholder(null),
    getSearchBarPlaceholder,
    learnerFilter: undefined,
  };
}
